import { ContentCompletionByMonth } from "@coaching-culture/types";
import {
  Flex,
  GraphStream,
  Legend,
  LineChart,
  Panel,
  Text,
} from "@coaching-culture/ui";
import { addMonths, differenceInMonths } from "date-fns";
import { minBy, range } from "lodash";
import { useMemo } from "react";

type CompletionsWidgetProps = {
  completionData: ContentCompletionByMonth[];
};

export const CompletionsWidget = ({
  completionData,
}: CompletionsWidgetProps) => {
  const data = useMemo<GraphStream>(() => {
    const all = completionData ?? [];

    const earliest = minBy(all, (x) => x.year * 12 + x.month);

    if (earliest == null) {
      return [];
    }

    const earliestDate = new Date(earliest.year, earliest.month - 1, 1);
    const d = differenceInMonths(new Date(), earliestDate);

    const r = range(d)
      .map((x) => addMonths(new Date(), -x))
      .map((x) => ({
        month: x.getMonth() + 1,
        year: x.getFullYear(),
      }))
      .reverse();

    const getCumulative = (
      coll: ContentCompletionByMonth[],
      x: { month: number; year: number },
    ) =>
      coll
        .filter(
          (y) => (y.month <= x.month && y.year === x.year) || y.year < x.year,
        )
        .map((x) => x.count)
        .reduce((acc, val) => acc + val, 0);

    const ms = all.filter((x) => x.type === "module");
    const ls = all.filter((x) => x.type === "lesson");
    const as = all.filter((x) => x.type === "assessment");
    const lc = all.filter((x) => x.type === "learncast");

    const fn = getCumulative;

    return r.map((x) => [
      `${x.month}/${x.year.toString().substring(2)}`,
      [fn(ms, x), fn(ls, x), fn(as, x), fn(lc, x)],
    ]);
  }, [completionData]);

  return (
    <>
      <Flex justifyContent="space-between" alignItems="flex-end" mb={2}>
        <Text fontSize={5}>Completions</Text>
      </Flex>
      <Panel p={3} pt={0} mb={5}>
        <LineChart
          xAxis="Date"
          yAxis="Count"
          data={data}
          maxXLabels={24}
          streamLabels={["Mindset", "Lessons", "Assessments", "LearnCast"]}
        />
        <Flex justifyContent={"center"}>
          <Legend items={["Mindset", "Lessons", "Assessments", "LearnCast"]} />
        </Flex>
      </Panel>
    </>
  );
};
